import { Permission } from '@today/types'
import type { AppSidebarGroup, NavigationPredicate } from '@today/ui-components'

/**
 * Tenant Admin plane (AD-4) sidebar navigation — the full inventory IA from
 * `docs/product/ui-specs/tenant-admin/inventory.md`, grouped and ordered as the
 * inventory defines. A function of the viewer's OWN tenant slug because every
 * target is slug-scoped (`/t/{slug}/admin/...`); the slug comes from `/auth/me`
 * (`useViewerPermissions().tenantSlug`), never the URL `:slug` param, so the nav
 * can never point at another tenant (AD-8 / ADR-0032).
 *
 * Each item self-gates on the read permission atom its destination enforces
 * (ADR-0063: gating is on the atom, never a role) — reusing the atom↔section
 * mapping already shipped in `modules/admin-sections/catalog.ts` for the sections
 * that exist today. The groups are consumed by `AppSidebar` (TO-633); the
 * predicate names map to {@link tenantAdminNavPredicates}.
 *
 * **WIP contract.** A nav entry whose destination screen is not yet built targets
 * the shared `/error/not-found` route and carries `badge: 'WIP'`; the badge chip is
 * rendered by `TenantAdminShell`'s own `renderLink`. When a screen ships (e.g.
 * Users → TO-673), its ticket flips that item here from the WIP form to its real
 * `/t/{slug}/admin/...` path and drops the badge, and updates the nav test. The
 * inventory's **Governance** group (Content, Compliance, Audit log) is omitted —
 * those are cross-plane / platform-plane / routeless links, out of scope for the
 * shell-mount ticket (TO-635 red-team RT-003).
 */
/** A not-yet-built destination: route to the shared Not-Found page, flagged WIP. */
const WIP = { target: '/error/not-found', badge: 'WIP' } as const

/**
 * Assembles the tenant-admin nav for one tenant slug. Each group is built by its
 * own small factory below so the inventory IA reads one group at a time (and no
 * single function grows past the complexity threshold).
 */
export function tenantAdminNavGroups(slug: string): AppSidebarGroup[] {
	const base = `/t/${slug}/admin`
	return [
		overviewGroup(),
		peopleGroup(base),
		identityGroup(base),
		brandTemplatesGroup(base),
		channelsGroup(base),
		aiGroup(),
		settingsGroup(base),
	]
}

function overviewGroup(): AppSidebarGroup {
	return { key: 'overview', items: [{ label: 'Overview', ...WIP }] }
}

function peopleGroup(base: string): AppSidebarGroup {
	return {
		key: 'people',
		label: 'People',
		items: [
			{ label: 'Users and roles', visibleIf: 'can-read-users', ...WIP },
			{ label: 'Organization units', target: `${base}/config/organization`, visibleIf: 'can-read-tenant-config' },
		],
	}
}

function identityGroup(base: string): AppSidebarGroup {
	return {
		key: 'identity',
		label: 'Identity',
		items: [
			{ label: 'Identity & login', target: `${base}/sso`, visibleIf: 'can-read-identity' },
			{ label: 'Sign-in policy', visibleIf: 'can-read-identity', ...WIP },
		],
	}
}

function brandTemplatesGroup(base: string): AppSidebarGroup {
	return {
		key: 'brand-templates',
		label: 'Brand & templates',
		items: [
			{ label: 'Brand', target: `${base}/config/brand`, visibleIf: 'can-read-tenant-config' },
			{
				label: 'Email + SMS templates',
				target: `${base}/notification-templates`,
				visibleIf: 'can-read-notification-templates',
			},
			{ label: 'Landing pages', visibleIf: 'can-read-notification-templates', ...WIP },
			{ label: 'PDF watermark', target: `${base}/config/pdf-watermark`, visibleIf: 'can-read-tenant-config' },
		],
	}
}

function channelsGroup(base: string): AppSidebarGroup {
	return {
		key: 'channels',
		label: 'Channels & integrations',
		items: [
			{ label: 'Email', target: `${base}/config/email`, visibleIf: 'can-read-tenant-config' },
			{ label: 'SMS', target: `${base}/config/sms`, visibleIf: 'can-read-tenant-config' },
			{ label: 'Webhooks', visibleIf: 'can-read-webhooks', ...WIP },
			{ label: 'File exchange + imports', target: `${base}/imports`, visibleIf: 'can-read-imports' },
		],
	}
}

function aiGroup(): AppSidebarGroup {
	return { key: 'ai', label: 'AI', items: [{ label: 'AI', visibleIf: 'can-read-ai', ...WIP }] }
}

function settingsGroup(base: string): AppSidebarGroup {
	return {
		key: 'settings',
		label: 'Settings',
		items: [
			{ label: 'General', target: `${base}/config/general`, visibleIf: 'can-read-tenant-config' },
			{ label: 'Locales', target: `${base}/locale-settings`, visibleIf: 'can-read-locales' },
			{ label: 'Seeding status', target: `${base}/config/seeding`, visibleIf: 'can-read-tenant-config' },
		],
	}
}

/** True when the viewer holds any of the given atoms. */
function holdsAny(permissions: readonly string[], ...atoms: Permission[]): boolean {
	return atoms.some((atom) => permissions.includes(atom))
}

/**
 * Closed-set predicate map for {@link tenantAdminNavGroups}, resolved by
 * `AppSidebar`. Each predicate decides visibility from the viewer's permission
 * atoms alone (ADR-0063 — no role literal). The atoms mirror the read atom each
 * section's destination enforces; the config-console tabs (Organization, Brand,
 * PDF watermark, Email, SMS, General, Seeding) share the console floor
 * `tenant_config.manage`, matching the shipped `ADMIN_SECTION_CATALOG`.
 *
 * Overview has no predicate: it is the tenant-admin landing/digest, shown to any
 * authenticated tenant-admin viewer; its own screen guard enforces access.
 */
export const tenantAdminNavPredicates: Record<string, NavigationPredicate> = {
	'can-read-users': (permissions) => holdsAny(permissions, Permission.USER_READ),
	'can-read-tenant-config': (permissions) => holdsAny(permissions, Permission.TENANT_CONFIG_MANAGE),
	// Identity & login + Sign-in policy: the tenant's own ORG_ADMIN manages IdP;
	// a read-mostly PLATFORM_ADMIN holds the read atom (ADR-0063).
	'can-read-identity': (permissions) => holdsAny(permissions, Permission.TENANT_MANAGE_IDP, Permission.TENANT_IDP_READ),
	'can-read-notification-templates': (permissions) => holdsAny(permissions, Permission.NOTIFICATION_TEMPLATES_MANAGE),
	'can-read-webhooks': (permissions) => holdsAny(permissions, Permission.WEBHOOK_READ),
	'can-read-imports': (permissions) =>
		holdsAny(permissions, Permission.IMPORT_EXECUTION_READ, Permission.FILE_EXCHANGE_MANAGE),
	'can-read-ai': (permissions) => holdsAny(permissions, Permission.PROMPT_READ),
	'can-read-locales': (permissions) => holdsAny(permissions, Permission.TENANT_MANAGE_SETTINGS),
}
